import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { 
  Box, Alert, IconButton, Slider, LinearProgress, Typography 
} from '@mui/material';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';
import { MarketPodcast } from '@horwath-htl/shared-types';
import { PlayArrow, Pause, VolumeUp } from '@mui/icons-material';
import { ApiClient } from '../../services/api';

interface SimplePodcastPlayerProps {
  podcast: MarketPodcast;
}

export const SimplePodcastPlayer: React.FC<SimplePodcastPlayerProps> = ({ podcast }) => {
  const audioRef = React.useRef<HTMLAudioElement>(null);
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(podcast.durationSeconds || 0);
  const [volume, setVolume] = React.useState(1);
  const [streamUrl, setStreamUrl] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const apiClient = new ApiClient({ authenticatedFetch });

  // Reset state when podcast changes
  React.useEffect(() => {
    setStreamUrl(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setError(null);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [podcast.id]);

  const streamMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.get(
        `${API_URL}/api/marketcompass/markets/${podcast.marketId}/podcasts/${podcast.id}/stream-url`
      );
      return response.data;
    },
    onSuccess: (data) => {
      if (data.streamUrl) {
        setError(null);
        setStreamUrl(data.streamUrl);
        if (audioRef.current) {
          audioRef.current.src = data.streamUrl;
          // Add a small delay before playing to ensure the source is set
          setTimeout(() => {
            if (audioRef.current) {
              audioRef.current.play().catch(error => {
                setIsPlaying(false);
                
                // Check error type
                if (error.name === 'NotAllowedError' || error.name === 'AbortError') {
                  setError('Audio playback was blocked. Please try again.');
                } else {
                  setError('Failed to play audio. Please try again.');
                }
              });
            }
          }, 100);
          setIsPlaying(true);
        }
      }
    },
    onError: (error: any) => {
      setIsPlaying(false);
      setError('Failed to load podcast. Please try again.');
    }
  });

  const handlePlayPause = () => {
    if (!streamUrl && !streamMutation.isPending) {
      streamMutation.mutate();
    } else if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (event: Event, value: number | number[]) => {
    const time = value as number;
    setCurrentTime(time);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
    }
  };

  const handleVolumeChange = (event: Event, value: number | number[]) => {
    const vol = value as number;
    setVolume(vol);
    if (audioRef.current) {
      audioRef.current.volume = vol;
    }
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
        onError={(e) => {
          const audio = e.currentTarget as HTMLAudioElement;
          
          // Reset playing state on error
          setIsPlaying(false);
          
          // Common error codes:
          // 1 = MEDIA_ERR_ABORTED
          // 2 = MEDIA_ERR_NETWORK
          // 3 = MEDIA_ERR_DECODE
          // 4 = MEDIA_ERR_SRC_NOT_SUPPORTED
          if (audio.error?.code === 4) {
            setError('Unable to load audio file. Please try again.');
          } else if (audio.error?.code === 2) {
            setError('Network error loading audio. Please check your connection.');
          } else {
            setError('Failed to load audio. Please try again.');
          }
        }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton
          onClick={handlePlayPause}
          disabled={streamMutation.isPending}
          color="primary"
        >
          {isPlaying ? <Pause /> : <PlayArrow />}
        </IconButton>

        <Box sx={{ flex: 1, mx: 2 }}>
          <Slider
            value={currentTime}
            max={duration}
            onChange={handleSeek}
            disabled={!streamUrl}
            size="small"
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">
              {formatDuration(currentTime)}
            </Typography>
            <Typography variant="caption">
              {formatDuration(duration)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', width: 120 }}>
          <VolumeUp sx={{ mr: 1 }} />
          <Slider
            value={volume}
            max={1}
            step={0.1}
            onChange={handleVolumeChange}
            size="small"
          />
        </Box>
      </Box>

      {streamMutation.isPending && (
        <LinearProgress sx={{ mt: 2 }} />
      )}
      
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </>
  );
};